/**
 * Página de la ruta /exclusive (FullscreenExclusive.tsx = /exclusive).
 * Video Mux + Stripe **subscription** (membresía mensual). El pack "exclusive" pasa por
 * /api/create-subscription-checkout (hosted Stripe Checkout) — antes era PaymentIntent one-time.
 *
 * Precio: viene de `membership_products.stripe_price_id` para `plan_tier=vip`
 * (admin lo pega en /admin/suscripciones). Si no está, fallback al catálogo en
 * `@/config/stripe` (pack `exclusive`, $39.99 USD por defecto).
 *
 * Comportamiento exclusivo de esta ruta (no afecta a /sorpresa, /canal, etc.):
 * - Sin aviso "activar sonido" (hideUnmutePrompt solo aquí).
 * - Countdown global de 2h (mismo fin para todos, desde EXCLUSIVE_COUNTDOWN_END).
 * - Cupón Stripe del primer mes: 50% baseline (PROMO50, duration once) + watcher luck % opcional.
 *
 * Logo del checkout: env `STRIPE_CHECKOUT_LOGO_URL` (backend). Source: `anaafernandez`
 * (BRAND_SOURCE_KEY) — automáticamente en metadata del checkout y subscription.
 *
 * Playback ID y countdown: /api/public-playback-ids (EXCLUSIVE_PLAYBACK_ID, EXCLUSIVE_COUNTDOWN_END).
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const FALLBACK_PLAYBACK_ID = "PhCWZCQmP6hVfAuCokc00jipal401oe2M38PVvBW9NSas";

const buildTimePlaybackId =
  (import.meta.env.VITE_EXCLUSIVE_PLAYBACK_ID as string)?.trim() ||
  FALLBACK_PLAYBACK_ID;

function useCountdown(endAtIso: string | null) {
  const [remaining, setRemaining] = useState<{
    h: number;
    m: number;
    s: number;
  } | null>(null);

  useEffect(() => {
    if (!endAtIso || typeof endAtIso !== "string") return;
    const end = new Date(endAtIso).getTime();
    if (Number.isNaN(end)) return;

    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      if (diff <= 0) {
        setRemaining(null);
        return;
      }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setRemaining({ h, m, s });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endAtIso]);

  return remaining;
}

export default function FullscreenExclusive() {
  const [searchParams] = useSearchParams();
  // Optional admin-generated invite. When present we validate against
  // /api/invitation-codes/validate?probe=1 (which doesn't consume a use)
  // and gate the page behind it — same pattern as /exclusive-ar.
  const inviteCode = (searchParams.get("invite") || searchParams.get("code") || "").toUpperCase().trim();

  const [playbackId, setPlaybackId] = useState(buildTimePlaybackId);
  const [countdownEnd, setCountdownEnd] = useState<string | null>(null);
  const { setTemplateName } = useCurrentTemplate();
  const countdown = useCountdown(countdownEnd);

  // ── Invite validation state — only runs when ?invite=CODE is present ──
  type InviteState =
    | { kind: "no-invite" }
    | { kind: "checking" }
    | { kind: "valid"; expires_at: string | null }
    | { kind: "invalid"; reason: "expired" | "exhausted" | "inactive" | "not_found" | "error" };
  const [inviteState, setInviteState] = useState<InviteState>(
    inviteCode ? { kind: "checking" } : { kind: "no-invite" }
  );

  useEffect(() => {
    if (!inviteCode) return;
    let cancelled = false;
    fetch(`/api/invitation-codes/validate?code=${encodeURIComponent(inviteCode)}&probe=1`)
      .then((r) => r.json())
      .then((data: { valid?: boolean; reason?: InviteState extends { kind: "invalid"; reason: infer R } ? R : never; valid_until_iso?: string }) => {
        if (cancelled) return;
        if (data.valid) {
          setInviteState({ kind: "valid", expires_at: data.valid_until_iso ?? null });
          // Use the invite's actual expiration as the page countdown so
          // burst links (60 s, 2 min) display real urgency instead of the
          // generic 2-hour /exclusive marketing timer.
          if (data.valid_until_iso) {
            setCountdownEnd(data.valid_until_iso);
          }
        } else {
          const reason = (data.reason ?? "not_found") as "expired" | "exhausted" | "inactive" | "not_found" | "error";
          setInviteState({ kind: "invalid", reason });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Fail open so a flaky DB doesn't block paying users.
        setInviteState({ kind: "valid", expires_at: null });
      });
    return () => { cancelled = true; };
  }, [inviteCode]);

  // Auto-expire on countdown end — only for invite-gated visits.
  useEffect(() => {
    if (inviteState.kind !== "valid" || !inviteState.expires_at) return;
    const expiresAtMs = new Date(inviteState.expires_at).getTime();
    if (!Number.isFinite(expiresAtMs)) return;
    const remaining = expiresAtMs - Date.now();
    if (remaining <= 0) {
      setInviteState({ kind: "invalid", reason: "expired" });
      return;
    }
    const timer = window.setTimeout(() => {
      setInviteState({ kind: "invalid", reason: "expired" });
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [inviteState]);

  // ── Funnel tracking — admin/live-funnel + Slack #anaafernandez ─────────
  // /exclusive can be hit either via an admin invite (DM-shared with
  // ?invite=CODE) or organically. The `has_invite` prop lets the operator
  // distinguish "DM landed" from "organic" in the live feed.
  const sessionIdRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `fe_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  );
  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current || inviteState.kind === "checking") return;
    if (typeof window === "undefined") return;
    viewTrackedRef.current = true;
    const ph = (window as unknown as { posthog?: { get_distinct_id?: () => string } }).posthog;
    mirrorFunnelEvent({
      event_name: "exclusive_view",
      posthog_distinct_id: ph?.get_distinct_id?.(),
      session_id: sessionIdRef.current,
      template: "FullscreenExclusive",
      template_variant: inviteCode ? "exclusive_invite" : "exclusive_organic",
      flow_name: "exclusive",
      landing_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      props: (() => {
        const expiresAt =
          inviteState.kind === "valid" ? (inviteState.expires_at ?? null) : null;
        const minutesRemaining = expiresAt
          ? Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 60_000))
          : null;
        const entryType = !inviteCode
          ? "organic"
          : inviteState.kind === "valid"
            ? "invite_valid"
            : inviteState.kind === "invalid"
              ? inviteState.reason === "expired"
                ? "invite_expired"
                : "invite_exhausted"
              : "invite_checking";
        return {
          has_invite: !!inviteCode,
          invite_state: inviteState.kind,
          entry_type: entryType,
          ...(inviteCode ? { invite_code: inviteCode } : {}),
          ...(expiresAt ? { expires_at: expiresAt } : {}),
          ...(minutesRemaining !== null ? { minutes_remaining: minutesRemaining } : {}),
          landing_label: inviteCode
            ? `invite_${entryType}${minutesRemaining !== null ? ` (${minutesRemaining}min)` : ""}`
            : "organic",
        };
      })(),
    });
  }, [inviteState.kind, inviteCode]);

  useEffect(() => {
    setTemplateName(null);
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
    };
  }, []);

  useEffect(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    if (!base) return;
    fetch(`${base}/api/public-playback-ids`)
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (
          data: { exclusive?: string; exclusiveCountdownEnd?: string } | null,
        ) => {
          const id = data?.exclusive?.trim();
          if (id) setPlaybackId(id);
          const end = data?.exclusiveCountdownEnd?.trim();
          if (end) setCountdownEnd(end);
        },
      )
      .catch(() => {});
  }, []);

  // ── Invitation-expired wall ─────────────────────────────────────────────
  // When an admin-generated invitation link is invalid, block the checkout
  // and explain why. Organic traffic (no ?invite= param) skips this entirely.
  if (inviteState.kind === "invalid") {
    const headline =
      inviteState.reason === "expired"
        ? "Esta invitación ya expiró"
        : inviteState.reason === "exhausted"
          ? "Esta invitación alcanzó su límite de usos"
          : inviteState.reason === "inactive"
            ? "Esta invitación fue desactivada"
            : "Invitación no válida";
    return (
      <div className="w-full h-dvh max-h-dvh flex items-center justify-center bg-black px-6 text-center">
        <div className="max-w-md space-y-4">
          <div className="text-5xl">⏳</div>
          <h1 className="text-2xl font-semibold text-white">{headline}</h1>
          <p className="text-zinc-400 text-sm">
            If you got this link from us, please ask for a new one. If you
            think this is an error, contact us by DM.
          </p>
          <a
            href="/"
            className="inline-block rounded-full bg-white px-6 py-2 text-sm font-medium text-black hover:bg-zinc-200"
          >
            Back to home
          </a>
        </div>
      </div>
    );
  }

  // Loading state while we check the code (only if there IS one)
  if (inviteState.kind === "checking") {
    return (
      <div className="w-full h-dvh max-h-dvh flex items-center justify-center bg-black">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
      </div>
    );
  }

  return (
    <div className="w-full h-dvh max-h-dvh overflow-hidden bg-black relative">
      {/* Countdown — compact MM:SS for short invite-gated timers, full
          hh:mm:ss for the global 2h marketing timer. Color/pulse intensify
          as time runs out. Header copy switches when ?invite= is driving it. */}
      {countdown && (() => {
        const totalSeconds = countdown.h * 3600 + countdown.m * 60 + countdown.s;
        const isShort = totalSeconds < 60 * 5;
        const display = isShort
          ? `${String(countdown.m).padStart(2, "0")}:${String(countdown.s).padStart(2, "0")}`
          : `${String(countdown.h).padStart(2, "0")}:${String(countdown.m).padStart(2, "0")}:${String(countdown.s).padStart(2, "0")}`;
        const urgency =
          totalSeconds <= 10 ? "text-red-400 border-red-500/60 animate-pulse"
          : totalSeconds <= 30 ? "text-amber-300 border-amber-400/50"
          : totalSeconds <= 60 ? "text-amber-200 border-amber-300/30"
          : "text-white border-white/20";
        const isInviteCountdown = inviteState.kind === "valid" && !!inviteState.expires_at;
        return (
          <div
            className={`absolute top-4 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-xl bg-black/70 text-center backdrop-blur-sm border ${urgency}`}
            aria-live="polite"
            aria-label={`Time remaining ${display}`}
          >
            <span className="text-xs uppercase tracking-wider text-white/80 block mb-0.5">
              {isInviteCountdown ? "Tu invitación expira en" : "Termina en"}
            </span>
            <span className="font-body text-lg font-bold tabular-nums tracking-tight">
              {display}
            </span>
          </div>
        );
      })()}

      <MuxVideoPlayerWithCTA
        playbackId={playbackId}
        overridePackId="exclusive"
        variant="fullscreen"
        displayMode="reel"
        conversionReel={true}
        ctaLabel="Descubrir"
        ctaSubtitle="Ingresar al Canal Privado + Hablar conmigo"
        hideUnmutePrompt
        inviteCode={inviteCode || undefined}
      />
    </div>
  );
}
